import React, { useState, useEffect, useCallback } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";
import { ArrowCounterClockwise, LockSimple } from "@phosphor-icons/react";
import API from "../../../lib/api";

/**
 * Drawer for creating or editing a brand manually.
 *
 * Two modes:
 *   - `mode="create"` — empty form, POST /admin/brands/manual
 *   - `mode="edit"`   — prefilled from `brand` prop, PATCH /admin/brands/{cid}/manual
 *
 * Edit mode visually marks fields that admin has previously overridden
 * (read from `brand._manual_fields`) with a small lock icon next to
 * the label, plus an "Unlock" button that re-enables feed-sync writes
 * for that field.
 */
export default function ManualBrandDrawer({ open, onClose, mode = "create", brand = null, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && brand) {
      let domain = brand.domain || "";
      // Feed brands often only have advertiser_url / campaign_url —
      // derive a domain so Edit doesn't fail the name+domain required check.
      //
      // `tracking_link` is deliberately NOT in this list. On every network
      // that redirects (MaxBounty afflat3c1/afflat3c2, Siteplug, AdMedia,
      // PubAdHub) its host is the network's own redirector, identical across
      // the whole catalog — and because Domain is required and the drawer
      // submits the full form, saving would persist that host AND lock it
      // into `_manual_fields`, permanently. Better to leave Domain empty and
      // make the admin supply a real one.
      if (!domain) {
        for (const raw of [brand.advertiser_url, brand.campaign_url, brand.landing_page_sample]) {
          if (!raw || typeof raw !== "string") continue;
          try {
            const host = new URL(raw.startsWith("http") ? raw : `https://${raw}`).hostname;
            if (host) {
              domain = host.replace(/^www\./, "");
              break;
            }
          } catch {
            /* ignore */
          }
        }
      }
      setForm({
        name: brand.name || "",
        advertiser_name: brand.advertiser_name || "",
        domain,
        tracking_link: brand.tracking_link || "",
        deeplink_template: brand.deeplink_template || "",
        base_commission_pct: brand.base_commission_pct ?? 0,
        pub_flat_amount: brand.pub_flat_amount ?? brand.default_pub_flat_amount ?? "",
        offer_type: (brand.offer_type || "cpa").toLowerCase(),
        payout_display: brand.payout_display || "",
        payout_shape: brand.payout_shape || "",
        categories: (brand.categories || []).join(", "),
        image: brand.image || "",
        restricted_traffic: (brand.restricted_traffic || []).join(", "),
        geo_locations: (brand.geo_locations || []).join(", "),
        country_code: brand.country_code || "",
        description: brand.description || "",
        network: brand.network || "pubadhub",
      });
    } else if (open && !brand) {
      // Fresh create
      setForm({
        name: "", advertiser_name: "", domain: "", tracking_link: "",
        deeplink_template: "", base_commission_pct: 0, pub_flat_amount: "",
        offer_type: "cpa",
        payout_display: "", payout_shape: "", categories: "", image: "",
        restricted_traffic: "", geo_locations: "", country_code: "",
        description: "", network: "pubadhub",
      });
    }
  }, [open, brand]);

  const lockedFields = new Set(brand?._manual_fields || []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim() || !form.domain?.trim()) {
      toast.error("Name and domain are required");
      return;
    }
    // AdMedia tracking-link guard — matches the backend rule. Catching
    // it client-side avoids a round-trip + keeps the inline error
    // attached to the field the admin actually edited.
    if (form.network === "admedia" || brand?.network === "admedia") {
      const link = (form.tracking_link || "").trim();
      if (!isEdit && !link) {
        toast.error("AdMedia tracking URL is required");
        return;
      }
      if (link && !link.includes("[SUBID]")) {
        toast.error("AdMedia tracking URL must contain the `[SUBID]` macro — we substitute it with the pub's sub_id1 at click time.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_commission_pct: parseFloat(form.base_commission_pct) || 0,
        categories: form.categories ? form.categories.split(",").map((s) => s.trim()).filter(Boolean) : [],
        restricted_traffic: form.restricted_traffic ? form.restricted_traffic.split(",").map((s) => s.trim()).filter(Boolean) : [],
        geo_locations: form.geo_locations ? form.geo_locations.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) : [],
      };
      const isCpc = (form.offer_type || "").toLowerCase() === "cpc";
      if (isCpc) {
        const flatRaw = (form.pub_flat_amount ?? "").toString().trim();
        payload.pub_flat_amount = flatRaw === "" ? 0 : parseFloat(flatRaw);
        if (!Number.isFinite(payload.pub_flat_amount) || payload.pub_flat_amount < 0) {
          toast.error("Pub flat $ must be 0 or greater");
          setSaving(false);
          return;
        }
        // CPC brands don't use % commission — zero it so catalog stays clean.
        payload.base_commission_pct = 0;
      } else {
        delete payload.pub_flat_amount;
      }
      if (isEdit) {
        delete payload.network; // network can't change after creation
        const r = await API.patch(`/admin/brands/${brand.campaign_id}/manual`, payload);
        // Surface how many publisher assignments were re-synced with
        // the new name so admin sees the rename propagated everywhere.
        const cascaded = r?.data?.assignments_updated || 0;
        const renamed = (brand?.name || "") !== (form.name || "");
        if (renamed && cascaded > 0) {
          toast.success(`Updated "${form.name}" — synced ${cascaded} assignment${cascaded === 1 ? "" : "s"}`);
        } else {
          toast.success(`Updated "${form.name}"`);
        }
      } else {
        await API.post("/admin/brands/manual", payload);
        toast.success(`Created "${form.name}"`);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const unlockField = async (field) => {
    if (!brand?.campaign_id) return;
    try {
      await API.delete(`/admin/brands/${brand.campaign_id}/manual-fields/${field}`);
      toast.success(`Unlocked "${field}" — next sync will overwrite it`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unlock failed");
    }
  };

  const FieldLabel = ({ field, children }) => (
    <div className="flex items-center justify-between mb-1">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
        {children}
      </Label>
      {isEdit && lockedFields.has(field) && (
        <button
          type="button"
          onClick={() => unlockField(field)}
          className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-amber-700 hover:text-amber-900 font-bold"
          title="This field is locked from feed-sync overwrites — click to re-enable sync for it"
          data-testid={`unlock-${field}`}
        >
          <LockSimple size={10} weight="fill" /> locked
        </button>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-xl" data-testid="manual-brand-drawer">
        <SheetHeader>
          <SheetTitle>{isEdit ? `Edit ${brand?.name || "brand"}` : "Add new brand"}</SheetTitle>
          <SheetDescription>
            {isEdit ? (
              <>Manual edits are protected — next catalog sync skips fields you change. <LockSimple size={10} className="inline" /> = locked from sync.</>
            ) : (
              <>Create a brand for PubAdHub / PubAdHub 2 / Shopnomix InMobi / AdMedia. The campaign_id is auto-generated with a <code>_manual_</code> marker.</>
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 py-4">
          {!isEdit && (
            <div>
              <FieldLabel field="network">Network</FieldLabel>
              <Select value={form.network} onValueChange={(v) => set("network", v)}>
                <SelectTrigger className="h-9" data-testid="manual-brand-network"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pubadhub">PubAdHub</SelectItem>
                  <SelectItem value="pubadhub_kevin">PubAdHub 2</SelectItem>
                  <SelectItem value="shopnomix_kevin">Shopnomix InMobi</SelectItem>
                  <SelectItem value="admedia">AdMedia</SelectItem>
                  <SelectItem value="manual">Manual (no feed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel field="name">Name *</FieldLabel>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="manual-brand-name" />
            </div>
            <div>
              <FieldLabel field="domain">Domain *</FieldLabel>
              <Input value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder="example.com" data-testid="manual-brand-domain" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel field="offer_type">Offer type</FieldLabel>
              <Select value={form.offer_type} onValueChange={(v) => set("offer_type", v)}>
                <SelectTrigger className="h-9" data-testid="manual-brand-offer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cpa">CPA</SelectItem>
                  <SelectItem value="cpc">CPC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.offer_type || "").toLowerCase() === "cpc" ? (
              <div>
                <FieldLabel field="pub_flat_amount">Pub flat $</FieldLabel>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.pub_flat_amount}
                  onChange={(e) => set("pub_flat_amount", e.target.value)}
                  placeholder="e.g. 0.25"
                  data-testid="manual-brand-pub-flat"
                />
              </div>
            ) : (
              <div>
                <FieldLabel field="base_commission_pct">Base commission %</FieldLabel>
                <Input type="number" step="0.1" value={form.base_commission_pct} onChange={(e) => set("base_commission_pct", e.target.value)} data-testid="manual-brand-commission" />
              </div>
            )}
          </div>
          <div>
            <FieldLabel field="payout_display">
              {(form.offer_type || "").toLowerCase() === "cpc" ? "CPC label (publisher-facing)" : "Payout display (free-text)"}
            </FieldLabel>
            <Input
              value={form.payout_display}
              onChange={(e) => set("payout_display", e.target.value)}
              placeholder={(form.offer_type || "").toLowerCase() === "cpc" ? "e.g. upto $0.25" : "e.g. Up to 15% CPA, $0.25 CPC"}
              data-testid="manual-brand-payout-display"
            />
          </div>
          <div>
            <FieldLabel field="geo_locations">GEOs (comma-separated ISO-2)</FieldLabel>
            <Input value={form.geo_locations} onChange={(e) => set("geo_locations", e.target.value)} placeholder="US, CA, GB" data-testid="manual-brand-geos" />
          </div>
          <div>
            <FieldLabel field="categories">Categories (comma-separated)</FieldLabel>
            <Input value={form.categories} onChange={(e) => set("categories", e.target.value)} placeholder="Fashion, Travel" data-testid="manual-brand-categories" />
          </div>
          <div>
            <FieldLabel field="tracking_link">Tracking link{(form.network === "admedia" || brand?.network === "admedia") ? " *" : ""}</FieldLabel>
            <Input value={form.tracking_link} onChange={(e) => set("tracking_link", e.target.value)} data-testid="manual-brand-tracking-link" />
            {form.network === "shopnomix_kevin" && (
              <p className="mt-1 text-[10px] text-slate-500 leading-relaxed" data-testid="inmobi-macro-hint">
                Homepage traffic uses this Tracking link. When the pub passes <code className="text-slate-700">deepLink=</code>, we use Deeplink template instead (if set). Macros: <code className="text-slate-700">[click_id]</code>, <code className="text-slate-700">[pub_id]</code>, <code className="text-slate-700">[url]</code>, <code className="text-slate-700">[ref]</code>, <code className="text-slate-700">[source]</code>, <code className="text-slate-700">[query]</code>.
              </p>
            )}
            {(form.network === "admedia" || brand?.network === "admedia") && (
              <p className="mt-1 text-[10px] text-slate-500 leading-relaxed" data-testid="admedia-macro-hint">
                AdMedia macros: <code className="text-slate-700">[SUBID]</code> (required — we inject the pub's sub_id1),{" "}
                <code className="text-slate-700">[Q]</code>, <code className="text-slate-700">[CLICKID]</code>{" "}
                (from <code className="text-slate-700">?clickid=</code> or <code className="text-slate-700">?subId2=</code> — stored as sub_id2),{" "}
                <code className="text-slate-700">[REF]</code> (<code className="text-slate-700">?ref=</code> stored as sub_id3; else article referer / WP homepage).
                Pub template example:{" "}
                <code className="text-slate-700">?subId1=…&amp;q=&#123;Q&#125;&amp;clickid=&#123;CLICKID&#125;&amp;ref=&#123;REF&#125;</code>.
                We also re-parse <code className="text-slate-700">?cmp=…</code> from this URL on every save so the daily CSV report keeps mapping rows to this brand.
              </p>
            )}
          </div>
          <div>
            <FieldLabel field="deeplink_template">Deeplink template</FieldLabel>
            <Input value={form.deeplink_template} onChange={(e) => set("deeplink_template", e.target.value)} data-testid="manual-brand-deeplink" />
            {(form.network === "shopnomix_kevin" || brand?.network === "shopnomix_kevin") && (
              <p className="mt-1 text-[10px] text-slate-500 leading-relaxed" data-testid="inmobi-deeplink-hint">
                Used only when the pub passes <code className="text-slate-700">?deepLink=…</code>. Can be a different host than Tracking link. Include <code className="text-slate-700">[url]</code> for the product destination.
              </p>
            )}
          </div>
          <div>
            <FieldLabel field="image">Logo URL</FieldLabel>
            <Input value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="manual-brand-image" />
          </div>
          <div>
            <FieldLabel field="restricted_traffic">Restricted traffic types (comma-separated)</FieldLabel>
            <Input value={form.restricted_traffic} onChange={(e) => set("restricted_traffic", e.target.value)} placeholder="email, sms" data-testid="manual-brand-restricted" />
          </div>
          <div>
            <FieldLabel field="description">Description</FieldLabel>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="manual-brand-description" />
          </div>
        </div>
        <SheetFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="manual-brand-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002089] text-white" data-testid="manual-brand-save">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create brand"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}


/**
 * Audit history drawer — opens from the brand row's history icon.
 * Groups entries by field and offers a "Rollback to this value" button
 * on each historical change.
 */
export function BrandAuditDrawer({ open, onClose, brand, onRolledBack }) {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);

  const fetchAudit = useCallback(async () => {
    if (!brand?.campaign_id) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/brands/${brand.campaign_id}/audit`);
      setEntries(data.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }, [brand?.campaign_id]);

  useEffect(() => { if (open) fetchAudit(); }, [open, fetchAudit]);

  const handleRollback = async (auditId, field) => {
    if (!window.confirm(`Restore the previous value for "${field}"? This writes a new audit row.`)) return;
    try {
      await API.post(`/admin/brands/${brand.campaign_id}/audit/${auditId}/rollback`);
      toast.success(`Rolled back ${field}`);
      fetchAudit();
      onRolledBack?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    }
  };

  // Group entries by field so admin can scan one field's history at a time
  const grouped = entries.reduce((acc, e) => {
    (acc[e.field] = acc[e.field] || []).push(e);
    return acc;
  }, {});

  const fmt = (v) => {
    if (v === null || v === undefined) return <span className="text-slate-400 italic">none</span>;
    if (Array.isArray(v)) return v.join(", ") || <span className="text-slate-400 italic">empty</span>;
    if (typeof v === "object") return <code className="text-[10px]">{JSON.stringify(v).slice(0, 80)}</code>;
    return String(v);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-2xl" data-testid="audit-drawer">
        <SheetHeader>
          <SheetTitle>Audit history — {brand?.name}</SheetTitle>
          <SheetDescription>
            Every manual change, grouped by field. Rollback is forward-only — it writes a NEW audit row, never deletes history.
          </SheetDescription>
        </SheetHeader>
        <div className="py-4 space-y-5">
          {loading && <p className="text-sm text-slate-400">Loading…</p>}
          {!loading && entries.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">No audit entries yet. Edits to this brand will appear here.</p>
          )}
          {!loading && Object.entries(grouped).map(([field, items]) => (
            <div key={field} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`audit-group-${field}`}>
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                <span className="font-mono text-xs font-bold text-slate-800">{field}</span>
                <span className="text-[10px] text-slate-500 ml-2">{items.length} change{items.length === 1 ? "" : "s"}</span>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {items.map((e, i) => (
                    <tr key={e.audit_id} className="border-b border-slate-100 last:border-b-0" data-testid={`audit-row-${e.audit_id}`}>
                      <td className="px-3 py-2 text-[10px] text-slate-400 font-mono whitespace-nowrap">{e.at.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 text-[10px]">
                        <span className={`inline-block px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                          e.action === "create" ? "bg-emerald-50 text-emerald-700"
                          : e.action === "delete" ? "bg-rose-50 text-rose-700"
                          : e.action === "rollback" ? "bg-amber-50 text-amber-700"
                          : e.action === "unlock" ? "bg-slate-100 text-slate-600"
                          : "bg-blue-50 text-blue-700"
                        }`}>{e.action}</span>
                      </td>
                      <td className="px-3 py-2 text-[11px]">
                        <div className="text-slate-400">old: {fmt(e.old_value)}</div>
                        <div className="text-slate-800">new: {fmt(e.new_value)}</div>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{e.user_email}</td>
                      <td className="px-3 py-2 text-right">
                        {i > 0 && e.action !== "create" && (
                          <button
                            type="button"
                            onClick={() => handleRollback(e.audit_id, e.field)}
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900"
                            title="Restore the OLD value from this row"
                            data-testid={`rollback-${e.audit_id}`}
                          >
                            <ArrowCounterClockwise size={11} /> rollback
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
